import { format, parseISO } from 'date-fns';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AmcLambdaFetch, awaitJSON } from '@src/utils/fetch-utils';
import {
  AmcMediaMixGraphRow,
  AmcMediaMixResponse,
  AmcMediaMixTableRow
} from '../amc-media-mix-types';

const sanitizeTableRow = (row: AmcMediaMixTableRow): AmcMediaMixTableRow => ({
  ...row,
  reach: Number(row.reach),
  purchased: Number(row.purchased),
  purchaseRate: Number(row.purchaseRate),
  avgOrderSize: Number(row.avgOrderSize)
});

const sanitizeGraphRow = (row: AmcMediaMixGraphRow): AmcMediaMixGraphRow => ({
  ...row,
  reach: Number(row.reach),
  purchased: Number(row.purchased),
  avgOrderSize: Number(row.avgOrderSize),
  date: format(parseISO(row.date), 'MMM dd, yyyy')
});

const fetchAmcMediaMixData = async (
  amcInstanceId: string,
  startDate: string,
  endDate: string
): Promise<AmcMediaMixResponse> => {
  const response = await AmcLambdaFetch('/getMediaMixReport', {
    params: { amcInstanceId, startDate, endDate, duration: 'monthly' }
  });
  const json = await awaitJSON<AmcMediaMixResponse>(response);
  return {
    ...json,
    prospectiveTable: json.prospectiveTable.map(sanitizeTableRow),
    newToBrandTable: json.newToBrandTable.map(sanitizeTableRow),
    prospectiveGraph: json.prospectiveGraph.map(sanitizeGraphRow),
    newToBrandGraph: json.newToBrandGraph.map(sanitizeGraphRow)
  };
};

export const useAmcMediaMixData = (
  amcInstanceId: string | undefined,
  startDate: string,
  endDate: string
): ReturnType<typeof useQuery<AmcMediaMixResponse>> =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['amcMediaMixData', amcInstanceId, startDate, endDate],
    queryFn: () => {
      if (!amcInstanceId) throw new Error('Missing AMC instance id');
      return fetchAmcMediaMixData(amcInstanceId, startDate, endDate);
    },
    enabled: Boolean(amcInstanceId),
    placeholderData: keepPreviousData
  });
